import React from "react";
import { SearchGlyph, GLYPH } from "./symbols";

// =====================================================================
// Club Radar V2 — buttons, inputs & selection controls (DESIGN-SPEC §3
// CHECKBOX/RADIO/DASH-BOX, §4 items 2/6/16). Purely presentational:
// state and handlers are wired by the callers (existing logic only,
// Brief Rules 6/8). All copy arrives as children/props.
// =====================================================================

const buttonBase = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  boxSizing: "border-box",
  whiteSpace: "nowrap",
  cursor: "pointer",
  fontFamily: "inherit",
  border: "none",
};

// ── Primary button — bg `#2283FF`, white, 700 (h36–46, r10–12) ───────
export const PrimaryButton = ({ height = 38, radius = 10, fontSize = 13, pad = "0 15px", style, children, ...rest }) => (
  <button
    type="button"
    style={{
      ...buttonBase,
      height,
      borderRadius: radius,
      padding: pad,
      background: "#2283FF",
      color: "#fff",
      fontSize,
      fontWeight: 700,
      ...(rest.disabled ? { opacity: 0.5, cursor: "default" } : null),
      ...style,
    }}
    {...rest}
  >
    {children}
  </button>
);

// Profile-header CTA variant — bg `#1B6FE0`; `pressed` adds the open-
// picker focus ring `0 0 0 3px #E8F1FF` (frame 1l).
export const ProfileCtaButton = ({ pressed = false, height = 38, radius = 10, fontSize = 13.5, style, children, ...rest }) => (
  <button
    type="button"
    style={{
      ...buttonBase,
      height,
      borderRadius: radius,
      padding: "0 16px",
      gap: 7,
      background: "#1B6FE0",
      color: "#fff",
      fontSize,
      fontWeight: 700,
      ...(pressed ? { boxShadow: "0 0 0 3px #E8F1FF" } : null),
      ...style,
    }}
    {...rest}
  >
    {children}
  </button>
);

// ── Secondary button — white, border `#E5E7EB`, fg `#374151` ─────────
export const SecondaryButton = ({ height = 38, radius = 10, fontSize = 13, fontWeight = 700, color = "#374151", pad = "0 15px", style, children, ...rest }) => (
  <button
    type="button"
    style={{
      ...buttonBase,
      height,
      borderRadius: radius,
      padding: pad,
      background: "#fff",
      border: "1px solid #E5E7EB",
      color,
      fontSize,
      fontWeight,
      ...style,
    }}
    {...rest}
  >
    {children}
  </button>
);

// ── Danger button — bg `#DC2626`, white, 700 (confirms 1p/1q) ────────
export const DangerButton = ({ height = 38, radius = 10, fontSize = 13, pad = "0 15px", style, children, ...rest }) => (
  <button
    type="button"
    style={{
      ...buttonBase,
      height,
      borderRadius: radius,
      padding: pad,
      background: "#DC2626",
      color: "#fff",
      fontSize,
      fontWeight: 700,
      ...style,
    }}
    {...rest}
  >
    {children}
  </button>
);

// ── Text button — borderless (mobile "Cancel" h40 `#6B7280`, "Share"
// `#2283FF`) ─────────────────────────────────────────────────────────
export const TextButton = ({ height = 40, fontSize = 13, color = "#6B7280", style, children, ...rest }) => (
  <button
    type="button"
    style={{
      ...buttonBase,
      height,
      background: "transparent",
      color,
      fontSize,
      fontWeight: 700,
      ...style,
    }}
    {...rest}
  >
    {children}
  </button>
);

// ── Icon square button — ⋯ / × / ‹ triggers ──────────────────────────
// 28×28 r8 (table rows) · 30×30 r9 (drawer/mobile) · 34×34 r10 (list
// header). `active` = open-menu state (border 1.5 `#2283FF`, bg
// `#E8F1FF`, fg `#1B6FE0`, frame 1b). Default glyph color `#6B7280`
// (rows use `#9CA3AF` via `color`).
export const IconSquareButton = ({ size = 34, radius = 10, active = false, color = "#6B7280", fontSize = 15, style, children, ...rest }) => (
  <button
    type="button"
    style={{
      ...buttonBase,
      width: size,
      height: size,
      flex: "none",
      borderRadius: radius,
      background: active ? "#E8F1FF" : "#fff",
      border: active ? "1.5px solid #2283FF" : "1px solid #E5E7EB",
      color: active ? "#1B6FE0" : color,
      fontSize,
      fontWeight: 700,
      ...style,
    }}
    {...rest}
  >
    {children}
  </button>
);

// Convenience: the ⋯ overflow trigger (verbatim U+22EF).
export const OverflowButton = (props) => (
  <IconSquareButton {...props}>{GLYPH.ellipsisMenu}</IconSquareButton>
);

// ── Dashed CTA — "+ New list" (§4 item 3) ────────────────────────────
// h38 desktop / h44 mobile, border 1.5px dashed `#CBD5E1`, r10/12,
// `#2283FF` 13/13.5px 700, centered, gap 6.
export const DashedCtaButton = ({ height = 38, radius = 10, fontSize = 13, style, children, ...rest }) => (
  <button
    type="button"
    style={{
      ...buttonBase,
      height,
      width: "100%",
      gap: 6,
      borderRadius: radius,
      border: "1.5px dashed #CBD5E1",
      background: "transparent",
      color: "#2283FF",
      fontSize,
      fontWeight: 700,
      ...style,
    }}
    {...rest}
  >
    {children}
  </button>
);

// ── Search input — §4 item 2 ─────────────────────────────────────────
// h34–38, border 1px `#E5E7EB`, r10, bg `#F8FAFC` (in-panel) or `#fff`
// (mobile page); S1 glyph + placeholder `#9CA3AF` 12–13px. Behavior
// (value/onChange, results logic) is the platform's — Brief Rule 11.
export const SearchInput = ({ height = 36, bg = "#F8FAFC", iconSize = 13, fontSize = 13, placeholder, style, inputStyle, ...rest }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: 8,
      height,
      boxSizing: "border-box",
      border: "1px solid #E5E7EB",
      borderRadius: 10,
      padding: "0 10px",
      background: bg,
      ...style,
    }}
  >
    <SearchGlyph size={iconSize} style={{ flex: "none" }} />
    <input
      type="text"
      placeholder={placeholder}
      style={{
        flex: 1,
        minWidth: 0,
        border: "none",
        outline: "none",
        background: "transparent",
        fontSize,
        color: "#111827",
        fontFamily: "inherit",
        ...inputStyle,
      }}
      {...rest}
    />
  </div>
);

// ── CHECKBOX — square check control (1l/1m pickers, 2a/2b members) ───
// 18×18 r6 desktop / 19–20 r7 mobile, border 1.5. States:
//   unchecked → bg `#fff`, border `#D1D5DB`, no tick
//   checked   → bg+border `#2283FF`, ✓ white
//   already   → bg `#ECFDF5`, border `#A7F3D0`, ✓ `#047857`
//     ("Already in this list" — pre-checked look, not toggleable)
export const CrCheckbox = ({ state = "unchecked", size = 18, radius = 6, tickSize = 10, style, ...rest }) => {
  const looks = {
    unchecked: { background: "#fff", border: "1.5px solid #D1D5DB", color: "transparent" },
    checked: { background: "#2283FF", border: "1.5px solid #2283FF", color: "#fff" },
    already: { background: "#ECFDF5", border: "1.5px solid #A7F3D0", color: "#047857" },
  };
  const look = looks[state] || looks.unchecked;
  return (
    <span
      role="presentation"
      data-state={state}
      style={{
        width: size,
        height: size,
        boxSizing: "border-box",
        borderRadius: radius,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        flex: "none",
        fontSize: tickSize,
        fontWeight: 800,
        ...look,
        ...style,
      }}
      {...rest}
    >
      {state === "unchecked" ? null : GLYPH.check}
    </span>
  );
};

// Dashed "new list" affordance box (DASH-BOX, 1l/1m): 18–20px square,
// r6–7, border 1.5 dashed `#93C5FD`, "+" centered `#2283FF`.
export const DashedCheckboxBox = ({ size = 18, radius = 6, fontSize = 12, style, ...rest }) => (
  <span
    style={{
      width: size,
      height: size,
      boxSizing: "border-box",
      borderRadius: radius,
      border: "1.5px dashed #93C5FD",
      color: "#2283FF",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      flex: "none",
      fontSize,
      fontWeight: 700,
      ...style,
    }}
    {...rest}
  >
    {GLYPH.plus}
  </span>
);

// ── RADIO — 1n/1o conversation rows, 2a/2b tier rows ─────────────────
// Outer 18×18 desktop / 19×19 mobile circle, border 1.5 (`#2283FF`
// selected / `#D1D5DB` not); inner dot 9×9 (`#2283FF` / transparent).
export const CrRadio = ({ selected = false, size = 18, style, ...rest }) => (
  <span
    data-selected={selected ? "true" : "false"}
    style={{
      width: size,
      height: size,
      boxSizing: "border-box",
      borderRadius: "50%",
      border: `1.5px solid ${selected ? "#2283FF" : "#D1D5DB"}`,
      background: "#fff",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      flex: "none",
      ...style,
    }}
    {...rest}
  >
    <span
      style={{
        width: 9,
        height: 9,
        borderRadius: "50%",
        background: selected ? "#2283FF" : "transparent",
      }}
    />
  </span>
);
